import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
    AccessTimeOutlined,
    FlagOutlined,
    GroupsOutlined,
    ShowChartOutlined,
    TrendingDown,
    TrendingFlat,
    TrendingUp,
    VerifiedUserRounded,
    HubRounded,
    ErrorOutlineRounded,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { colors } from "../../theme/colors";
import utils from "../../universal/utils";
import {
    formatTrustScoreTrendDelta,
    getTrustScoreTrend30d,
    resolvePlatformOverallAverage,
    TREND_COLORS,
} from "../../utils/trustScoreCalculations";
import { EmptyDataState } from "../common/EmptyDataState";
import ReputationRadarChart from "./ReputationRadarChart";
import ReputationHistoryChart from "./ReputationHistoryChart";
import ReputationVsCommunity from "./ReputationVsCommunity";
import ProfileMatchOutcomeDeltas from "./ProfileMatchOutcomeDeltas";
import ProfileReputationChanges from "./ProfileReputationChanges";
import ReputationDimensionCard, {
    CARD_TOP_GLOW_SX,
    REPUTATION_CARD_SX,
    REPUTATION_DIMENSIONS,
} from "./ReputationDimensionCard";

const edgeBasePath = utils.getEdgeBasePath();

async function fetchJson(url, options) {
    const response = await fetch(url, options);
    const data = await response.json();
    return { ok: response.ok, status: response.status, data };
}

function formatReputationLoadError(result, fallback) {
    const message = String(result?.data?.message || "").trim();
    if (result?.status === 401 || /^unauthorized$/i.test(message)) {
        return "Sign in to view reputation data";
    }
    return message || fallback;
}

function getDimensions(trustScoreData) {
    return trustScoreData?.details?.dimensions ?? trustScoreData?.profile?.dimensions ?? null;
}

function getOverallScore(trustScoreData) {
    return trustScoreData?.badge?.overallScore ?? null;
}

function getPeakScore(dimensions, overallScore) {
    if (!dimensions) {
        return overallScore ?? null;
    }

    const highs = Object.values(dimensions)
        .map((dimension) => dimension?.allTimeHigh ?? dimension?.score)
        .filter((value) => Number.isFinite(value));

    if (!highs.length) {
        return overallScore ?? null;
    }

    return Math.max(...highs, overallScore ?? 0);
}

function getLastUpdatedLabel(trustScoreData) {
    const lastUpdated =
        trustScoreData?.details?.lastUpdated
        ?? trustScoreData?.profile?.lastScoreUpdate
        ?? trustScoreData?.profile?.lastUpdated
        ?? null;

    if (!lastUpdated) {
        return "—";
    }

    const date = new Date(lastUpdated);
    if (Number.isNaN(date.getTime())) {
        return "—";
    }

    return formatDistanceToNow(date, { addSuffix: true });
}

function getRatedByCount(trustScoreData) {
    if (Number.isFinite(trustScoreData?.profile?.commendationsReceived)) {
        return trustScoreData.profile.commendationsReceived;
    }

    return trustScoreData?.details?.scoringEvents?.last3Months ?? 0;
}

function TrustStatRow({ icon: Icon, label, value }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
                py: "0.7rem",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                "&:last-of-type": {
                    borderBottom: "none",
                    pb: 0,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.55rem", minWidth: 0 }}>
                <Box
                    sx={{
                        width: "1.15rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Icon sx={{ fontSize: "1rem", color: "rgba(255,255,255,0.42)" }} />
                </Box>
                <Typography
                    sx={{
                        fontSize: "0.78rem",
                        fontWeight: 500,
                        color: "rgba(255,255,255,0.56)",
                        lineHeight: 1.3,
                    }}
                >
                    {label}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontSize: "0.82rem",
                    fontWeight: 800,
                    color: colors.text.primary,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                    flexShrink: 0,
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

const TREND_ICONS = {
    up: TrendingUp,
    down: TrendingDown,
    flat: TrendingFlat,
};

function ReputationTrendBanner({ history, overallScore }) {
    const trend = useMemo(
        () => getTrustScoreTrend30d(history, overallScore),
        [history, overallScore],
    );
    const TrendIcon = TREND_ICONS[trend.direction];
    const trendColor = trend.direction === "down" ? TREND_COLORS.down : TREND_COLORS.up;

    return (
        <Box
            sx={{
                mt: "3rem",
                px: "1rem",
                py: "1.25rem",
                borderRadius: "10px",
                border: "1px solid rgba(246,185,76,0.14)",
                backgroundColor: "rgba(255,255,255,0.02)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.55rem", minWidth: 0 }}>
                <Box
                    sx={{
                        width: "1.15rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <ShowChartOutlined sx={{ fontSize: "1rem", color: colors.primary.main }} />
                </Box>
                <Typography
                    sx={{
                        fontSize: "0.78rem",
                        fontWeight: 500,
                        color: "rgba(255,255,255,0.56)",
                        lineHeight: 1.3,
                    }}
                >
                    Trend (30 days)
                </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.3rem", flexShrink: 0 }}>
                <TrendIcon sx={{ fontSize: "1rem", color: trendColor }} />
                <Typography
                    sx={{
                        fontSize: "0.82rem",
                        fontWeight: 800,
                        color: trendColor,
                        lineHeight: 1,
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {formatTrustScoreTrendDelta(trend.delta)}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: trendColor,
                    lineHeight: 1,
                    flexShrink: 0,
                }}
            >
                {trend.label}
            </Typography>
        </Box>
    );
}

ReputationTrendBanner.propTypes = {
    history: PropTypes.array,
    overallScore: PropTypes.number,
};

TrustStatRow.propTypes = {
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default function ProfileReputationTab({ steamId, isOwnProfile }) {
    const [trustScoreData, setTrustScoreData] = useState(null);

    useEffect(() => {
        if (!steamId) {
            setTrustScoreData({ error: "No Steam ID available for this profile." });
            return undefined;
        }

        let cancelled = false;

        async function loadTrustScoreData() {
            setTrustScoreData({ loading: true, steamId, isOwnProfile });

            try {
                const authFetch = { credentials: "include" };
                const [
                    reputationResult,
                    reportsResult,
                    baselinesResult,
                    historyResult,
                ] = await Promise.all([
                    fetchJson(
                        isOwnProfile
                            ? `${edgeBasePath}/api/v1/reputation/me/detailed`
                            : `${edgeBasePath}/api/v1/reputation/${steamId}?detailed=true`,
                        authFetch,
                    ),
                    fetchJson(
                        `${edgeBasePath}/api/v1/reputation/${steamId}/reports-summary`,
                        authFetch,
                    ),
                    isOwnProfile
                        ? fetchJson(`${edgeBasePath}/api/v1/reputation/baseline`, authFetch)
                        : Promise.resolve({ ok: false, data: null }),
                    isOwnProfile
                        ? fetchJson(
                            `${edgeBasePath}/api/v1/reputation/history/${steamId}?limit=500`,
                            authFetch,
                        )
                        : Promise.resolve({ ok: false, data: { history: [] } }),
                ]);

                if (cancelled) return;

                const nextData = {
                    steamId,
                    isOwnProfile,
                    baselines: baselinesResult.data?.baselines ?? null,
                    reportsSummary: reportsResult.data?.summary ?? null,
                    history: historyResult.ok ? (historyResult.data?.history ?? []) : [],
                };

                if (isOwnProfile) {
                    if (!reputationResult.ok) {
                        throw new Error(
                            formatReputationLoadError(
                                reputationResult,
                                "Failed to load own reputation data",
                            ),
                        );
                    }

                    Object.assign(nextData, {
                        profile: reputationResult.data?.profile ?? null,
                        comparison: reputationResult.data?.comparison ?? null,
                        tips: reputationResult.data?.tips ?? null,
                        matchOutcomes: reputationResult.data?.matchOutcomes ?? null,
                        badge: reputationResult.data?.profile
                            ? {
                                steamId: reputationResult.data.profile.steamId,
                                trustTier: reputationResult.data.profile.trustTier,
                                overallScore: reputationResult.data.profile.overallTrustScore,
                            }
                            : null,
                        details: reputationResult.data?.profile
                            ? {
                                dimensions: reputationResult.data.profile.dimensions,
                                recentHistory: reputationResult.data.profile.recentHistory ?? [],
                                reporterStats: reputationResult.data.profile.reporterStats ?? null,
                                reportsByType: reputationResult.data.profile.reportsByType ?? null,
                                positiveStreaks: reputationResult.data.profile.positiveStreaks ?? null,
                                matchesPlayed: reputationResult.data.profile.matchesPlayed ?? null,
                                matchesCompleted: reputationResult.data.profile.matchesCompleted ?? null,
                                lastUpdated: reputationResult.data.profile.lastUpdated
                                    ?? reputationResult.data.profile.lastScoreUpdate
                                    ?? reputationResult.data.profile.createdAt
                                    ?? null,
                            }
                            : null,
                    });
                } else {
                    if (!reputationResult.ok) {
                        throw new Error(
                            formatReputationLoadError(
                                reputationResult,
                                "Failed to load reputation data",
                            ),
                        );
                    }

                    Object.assign(nextData, {
                        badge: reputationResult.data?.badge ?? null,
                        details: reputationResult.data?.details ?? null,
                    });
                }

                setTrustScoreData(nextData);
            } catch (error) {
                if (!cancelled) {
                    setTrustScoreData({
                        steamId,
                        isOwnProfile,
                        error: error.message || "Failed to load reputation data",
                    });
                }
            }
        }

        loadTrustScoreData();

        return () => {
            cancelled = true;
        };
    }, [steamId, isOwnProfile]);

    const summary = useMemo(() => {
        if (!trustScoreData || trustScoreData.loading || trustScoreData.error) {
            return null;
        }

        const dimensions = getDimensions(trustScoreData);
        const overallScore = getOverallScore(trustScoreData);

        return {
            overallScore,
            peakScore: getPeakScore(dimensions, overallScore),
            reportsCount: trustScoreData.reportsSummary?.totalReports ?? 0,
            lastUpdatedLabel: getLastUpdatedLabel(trustScoreData),
            ratedByCount: getRatedByCount(trustScoreData),
            dimensions,
            hasDimensions: !!dimensions,
            history: trustScoreData.history ?? [],
            baselines: trustScoreData.baselines,
            matchOutcomes: trustScoreData.isOwnProfile
                ? (trustScoreData.matchOutcomes ?? [])
                : [],
            recentHistory: trustScoreData.isOwnProfile
                ? (trustScoreData.details?.recentHistory ?? [])
                : [],
        };
    }, [trustScoreData]);

    if (trustScoreData?.loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} sx={{ color: colors.primary.main }} />
            </Box>
        );
    }

    if (trustScoreData?.error) {
        return (
            <EmptyDataState
                sectionTitle="Reputation"
                title="Unable to load reputation"
                icon={ErrorOutlineRounded}
                minHeight="14rem"
            >
                {trustScoreData.error}
            </EmptyDataState>
        );
    }

    if (!summary?.hasDimensions || summary.overallScore == null) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    width: "100%",
                    maxWidth: "100%",
                }}
            >
                <Box
                    sx={{
                        display: { xs: "flex", md: "grid" },
                        gridTemplateColumns: { md: "minmax(0, 22rem) minmax(0, 1fr)" },
                        flexDirection: { xs: "column", md: "unset" },
                        gap: "1rem",
                        width: "100%",
                        alignItems: "stretch",
                    }}
                >
                    <EmptyDataState
                        sectionTitle="Reputation"
                        sectionSubtitle="Overall trust score"
                        title="No reputation score"
                        icon={VerifiedUserRounded}
                        minHeight="16rem"
                    >
                        Play matches and complete your profile to unlock reputation
                    </EmptyDataState>
                    <EmptyDataState
                        sectionTitle="Reputation Breakdown"
                        sectionSubtitle="Dimension radar"
                        title="No breakdown available"
                        icon={HubRounded}
                        minHeight="16rem"
                    >
                        Reputation dimensions appear after enough rated activity
                    </EmptyDataState>
                </Box>
            </Box>
        );
    }

    const scorePercent = Math.max(0, Math.min(100, summary.overallScore));

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                width: "100%",
                maxWidth: "100%",
            }}
        >
            <Box
                sx={{
                    display: { xs: "flex", md: "grid" },
                    gridTemplateColumns: { md: "minmax(0, 22rem) minmax(0, 1fr)" },
                    flexDirection: { xs: "column", md: "unset" },
                    gap: "1rem",
                    width: "100%",
                    alignItems: "stretch",
                }}
            >
                <Box
                    sx={{
                        ...REPUTATION_CARD_SX,
                        width: "100%",
                        boxSizing: "border-box",
                    }}
                >
                    <Box sx={CARD_TOP_GLOW_SX} />
                    <Box sx={{ position: "relative", zIndex: 2 }}>
                        <Typography
                            sx={{
                                fontSize: "0.72rem",
                                fontWeight: 800,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: colors.text.primary,
                                lineHeight: 1,
                            }}
                        >
                            Reputation
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.35rem", mt: "1rem" }}>
                            <Typography
                                sx={{
                                    fontSize: "2.75rem",
                                    fontWeight: 800,
                                    lineHeight: 1,
                                    color: colors.primary.main,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {summary.overallScore}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    color: "rgba(255,255,255,0.42)",
                                    lineHeight: 1,
                                }}
                            >
                                /100
                            </Typography>
                        </Box>

                        <Box sx={{ mt: "0.85rem" }}>
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "0.45rem",
                                    borderRadius: "999px",
                                    backgroundColor: "rgba(255,255,255,0.08)",
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: `${scorePercent}%`,
                                        height: "100%",
                                        borderRadius: "999px",
                                        background: `linear-gradient(90deg, ${colors.primary.main}, ${colors.accent.main})`,
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mt: "0.45rem",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "0.68rem",
                                        fontWeight: 600,
                                        color: "rgba(255,255,255,0.38)",
                                    }}
                                >
                                    New
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "0.68rem",
                                        fontWeight: 600,
                                        color: "rgba(255,255,255,0.38)",
                                    }}
                                >
                                    Trusted
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                mt: "1.1rem",
                                pt: "0.35rem",
                                borderTop: "1px solid rgba(255,255,255,0.05)",
                            }}
                        >
                            <TrustStatRow
                                icon={FlagOutlined}
                                label="Reports (3mo)"
                                value={summary.reportsCount}
                            />
                            <TrustStatRow
                                icon={AccessTimeOutlined}
                                label="Last update"
                                value={summary.lastUpdatedLabel}
                            />
                            <TrustStatRow
                                icon={GroupsOutlined}
                                label="Rated by"
                                value={`${summary.ratedByCount} players`}
                            />
                            <TrustStatRow
                                icon={TrendingUp}
                                label="Peak score"
                                value={summary.peakScore ?? "—"}
                            />
                        </Box>

                        <ReputationTrendBanner
                            history={summary.history}
                            overallScore={summary.overallScore}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        ...REPUTATION_CARD_SX,
                        width: "100%",
                        boxSizing: "border-box",
                        minHeight: { md: "22rem" },
                    }}
                >
                    <Box sx={CARD_TOP_GLOW_SX} />
                    <ReputationRadarChart
                        dimensions={summary.dimensions}
                        overallScore={summary.overallScore}
                        reportsCount={summary.reportsCount}
                        lastUpdatedLabel={summary.lastUpdatedLabel}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: "1rem",
                    width: "100%",
                }}
            >
                {REPUTATION_DIMENSIONS.map(({ key, label, description, icon }) => {
                    const dimension = summary.dimensions?.[key] ?? {};
                    return (
                        <ReputationDimensionCard
                            key={key}
                            label={dimension.label ?? label}
                            score={dimension.score}
                            description={description}
                            icon={icon}
                        />
                    );
                })}
            </Box>

            {isOwnProfile ? (
                <Box
                    sx={{
                        display: { xs: "flex", lg: "grid" },
                        gridTemplateColumns: { lg: "minmax(0, 1fr) minmax(0, 30rem)" },
                        flexDirection: { xs: "column", lg: "unset" },
                        gap: "1rem",
                        width: "100%",
                        alignItems: "stretch",
                    }}
                >
                    <Box
                        sx={{
                            ...REPUTATION_CARD_SX,
                            width: "100%",
                            boxSizing: "border-box",
                            minHeight: { xs: "auto", lg: "22rem" },
                        }}
                    >
                        <Box sx={CARD_TOP_GLOW_SX} />
                        <ReputationHistoryChart
                            history={summary.history}
                            currentScore={summary.overallScore}
                            communityAvg={summary.baselines
                                ? resolvePlatformOverallAverage(summary.baselines)
                                : null}
                        />
                    </Box>

                    <Box
                        sx={{
                            ...REPUTATION_CARD_SX,
                            width: "100%",
                            boxSizing: "border-box",
                            overflow: "visible",
                            minHeight: { lg: "22rem" },
                        }}
                    >
                        <Box sx={CARD_TOP_GLOW_SX} />
                        <ReputationVsCommunity
                            dimensions={summary.dimensions}
                            overallScore={summary.overallScore}
                            baselines={summary.baselines}
                        />
                    </Box>
                </Box>
            ) : null}

            {isOwnProfile ? (
                <Box
                    sx={{
                        display: { xs: "flex", lg: "grid" },
                        gridTemplateColumns: { lg: "minmax(0, 1.55fr) minmax(0, 1fr)" },
                        flexDirection: { xs: "column", lg: "unset" },
                        gap: "1rem",
                        width: "100%",
                        alignItems: "stretch",
                    }}
                >
                    <Box
                        sx={{
                            ...REPUTATION_CARD_SX,
                            width: "100%",
                            boxSizing: "border-box",
                            minWidth: 0,
                        }}
                    >
                        <Box sx={CARD_TOP_GLOW_SX} />
                        <ProfileMatchOutcomeDeltas rows={summary.matchOutcomes} />
                    </Box>

                    <Box
                        sx={{
                            ...REPUTATION_CARD_SX,
                            width: "100%",
                            boxSizing: "border-box",
                            minWidth: 0,
                        }}
                    >
                        <Box sx={CARD_TOP_GLOW_SX} />
                        <ProfileReputationChanges history={summary.recentHistory} />
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
}
